import { Dispatch } from 'redux';
import CrudActionContainer from './CrudActionContainer';
import { StoreState } from '../store-interfaces';
import commonHandler from '../errorHandlers/common-handler';
import { getActionHookBuilder } from './action-hook-builder';
import MailchimpController from '../controllers/MailchimpController';
import {
  OauthUrlResponse,
  AccountsResponse,
  ListsResponse,
  ListMergeFieldsResponse,
} from '../model/MailchimpModel';
import { MAX_PAGE_SIZE } from '../model/PageModel';
import connectionsCRUDActions from './ConnectionsCRUDActions';
import { sortingListMergeFields } from '../helpers/sorting-helpers';

class MailchimpCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  retrieveAccounts: string;
  retrieveLists: string;
  retrieveMoreLists: string;
  retrieveListMergeFields: string;
  revokeAccess: string;
  resetStore: string;
  resetListMergeFieldsAction: string;

  constructor() {
    super('MAILCHIMP');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.retrieveLists = this.makeActionKey('RETRIEVED_LISTS');
    this.retrieveMoreLists = this.makeActionKey('RETRIEVED_MORE_LISTS');
    this.retrieveListMergeFields = this.makeActionKey(
      'RETRIEVED_LIST_MERGE_FIELDS',
    );
    this.revokeAccess = this.makeActionKey('REVOKE_ACCESS');
    this.resetStore = this.makeActionKey('RESET_STORE');
    this.resetListMergeFieldsAction = this.makeActionKey(
      'RESET_LIST_MERGE_FIELDS',
    );
  }

  getMailchimpOauthUrl(redirectTo: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse =
          await MailchimpController.getOauthUrl(redirectTo);
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  getAccounts(fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response: AccountsResponse =
          await MailchimpController.getAccounts(fbPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  getLists(id: number, offset: number = 0) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveLists));
      try {
        const response: ListsResponse = await MailchimpController.getLists(
          id,
          offset,
        );
        dispatch({
          type: this.retrieveLists,
          lists: response.lists,
          totalListItems: response.totalListItems,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  getMoreLists(id: number, offset: number = 0) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: ListsResponse = await MailchimpController.getLists(
          id,
          offset,
        );
        dispatch({
          type: this.retrieveMoreLists,
          lists: response.lists,
          totalListItems: response.totalListItems,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  getListMergeFields(accountId: number, id: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveListMergeFields));
      try {
        const response: ListMergeFieldsResponse =
          await MailchimpController.getListMergeFields(accountId, id);
        dispatch({
          type: this.retrieveListMergeFields,
          listMergeFields: sortingListMergeFields(response.listMergeFields),
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  disconnectAccount(accountId: number, fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAccess));
      try {
        const response = await MailchimpController.revokeAccess(accountId);

        dispatch({
          type: this.revokeAccess,
        });
        dispatch(this.getAccounts(fbPageId));
        dispatch(
          connectionsCRUDActions.retrieveAll(fbPageId, {
            type: 'mailchimp',
            pageSize: MAX_PAGE_SIZE,
          }),
        );
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  resetListMergeFields() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetListMergeFieldsAction });
    };
  }

  resetMailchimpStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }
}

const mailchimpCRUDActions = new MailchimpCRUDActions();

export default mailchimpCRUDActions;

export const useMailchimpCRUDActions = getActionHookBuilder(
  mailchimpCRUDActions,
  [
    'getMailchimpOauthUrl',
    'getAccounts',
    'disconnectAccount',
    'getMoreLists',
    'getLists',
    'getListMergeFields',
    'resetMailchimpStore',
    'resetListMergeFields',
  ],
);
